import React, { useState, Fragment, useEffect, useMemo, useCallback } from 'react';

import { FVIPipingItem, User, IncludeSummary } from '../../../utils/models';
import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { reArrangeInspectionItemAnomalies, reArrangeGeneralPhotos, validateWorkOrderNumber } from '../../../utils/utilities';
import { DEFAULT_REPORT_TITLE, MINIMUM_ITEM_SIZE_FOR_CHUNK_NOTICE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { getCurrentUser } from '../../../services/authService';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import Button from '../../themed/button';
import ChunkReport from '../../blocks/chunk-report';
import Conditional from '../../common/conditional';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomCertificateForm, { ICertificate } from '../custom-general/custom-certificates/form';
import Elevation from '../../themed/instruction-modals/elevation';
import ExecutiveSummaryForm from '../../forms/visual-inspection/executiveSummaryForm';
import FVICoverData from '../../themed/fvi-cover-data';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import Switch from '../../themed/switch';
import FVIPipingReport from './fviPipingReport';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import PipingItems from './piping-items';
import SyncTime from '../../blocks/sync-time';
import Zippy from '../../themed/zippy';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useIncompleteInspectedSections from '../../../hooks/useIncompleteInspectedSections';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CustomFVIPipingReport: React.FC = () => {
	const { activeTutorial } = useAppSelector(getTutorials);
	const { projectDetails, error } = useProjectDetails();
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const [certificates, setCertificates] = useState<ICertificate>();
	const [includeSummary, setIncludeSummary] = useState<IncludeSummary>('include');
	const [pipingItems, setPipingItems] = useState<FVIPipingItem[]>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [executiveSummary, setExecutiveSummary] = useState('');
	
	const [isChunkNoticeVisible, setChunkNoticeVisible] = useState(false);
	const [itemsToPublish, setItemsToPublish] = useState<FVIPipingItem[]>([]);

	const { role } = getCurrentUser() as User;
	const { isLastInspectedItemComplete } = useIncompleteInspectedSections(pipingItems);

	const reportDetails = useMemo(() => {
		return {
			projectDetails,
			reportTitle,
			certificates,
			coverData,
			executiveSummary,
			includeSummary,
			pipingItems,
		};
	}, [projectDetails, coverData, certificates, pipingItems, reportTitle, executiveSummary, includeSummary]);

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	const handleSaveAndPreview = (): void => {
		autoSave.saveReport();

		if (role.approveReports && pipingItems.length > MINIMUM_ITEM_SIZE_FOR_CHUNK_NOTICE && itemsToPublish.length === 0) {
			return setChunkNoticeVisible(true);
		}

		togglePreviewReport();
	};

	const handleToggleSummary = () => {
		setIncludeSummary((previous) => previous === 'include' ? 'exclude' : 'include');
	};

	const handleChangePipingItems = (newPipingItems: FVIPipingItem[]) => {
		setPipingItems(newPipingItems);
		autoSave.saveReport();
	};

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (!validateWorkOrderNumber(coverData.workOrderNumber))
				return toastService.error('Enter a valid work order number!');

			if (!isLastInspectedItemComplete)
				return toastService.error('Please fill all fields first!');
		}

		setPreviewReport((p: boolean) => !p);
	}, [isLastInspectedItemComplete, coverData.workOrderNumber]);

	const handleResetChunkedItemsAndClosePreview = useCallback(() => {
		setItemsToPublish([]);
		togglePreviewReport();
	}, [togglePreviewReport]);
 
	const handleCloseChunkNoticeAndPreviewReport = useCallback(() => {
		setChunkNoticeVisible(false);
		togglePreviewReport();
	}, [togglePreviewReport]);

	useEffect(() => {
		function initializeCache() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails | null;

			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);
				
				return;
			}

			if (cachedReport.includeSummary) {
				setIncludeSummary(cachedReport.includeSummary);
			}

			if (cachedReport.certificates) {
				setCertificates(cachedReport.certificates);
			}

			const rearrangedAnomalies = reArrangeInspectionItemAnomalies(cachedReport.pipingItems) as any[];
			const rearrangedGeneralPhotos = reArrangeGeneralPhotos(rearrangedAnomalies) as any[];
			const lastPhotoNumber = cachedReport.pipingItems.reduce((a, c) => a + c.generalPhotos.length, 0);
			
			dispatch(setLastPhotoNumber(lastPhotoNumber));
			setPipingItems(rearrangedGeneralPhotos);
			setCoverData(cachedReport.coverData?.coverData?.length > 0 ? cachedReport.coverData : fviCoverData);
			setReportTitle(cachedReport.reportTitle);
			setExecutiveSummary(cachedReport.executiveSummary);
		}

		initializeCache();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.EFVIP));
	}, [dispatch]);

	return (
		<Fragment>
			{projectDetails && (
				<ChunkReport
					projectId={projectDetails._id}
					projectModule={projectDetails.module}
					reportDetails={reportDetails}
					items={pipingItems}
					groupName="pipingItems"
					chunkKeyId="lineNumber" 
					isNoticeVisible={isChunkNoticeVisible}
					onSetChunkedItems={(items) => setItemsToPublish(items)}
					onCloseNotice={() => setChunkNoticeVisible(false)}
					onCancelChunkReport={handleCloseChunkNoticeAndPreviewReport} 
					setFullPreviewVisible={() => setPreviewReport(true)}
				/>
			)}

			<Modal isVisible={previewReport} onClose={handleResetChunkedItemsAndClosePreview}>
				<FVIPipingReport {...reportDetails} pipingItems={itemsToPublish.length > 0 ? itemsToPublish : pipingItems} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.exxonMobilFVIPiping[projectDetails.organisationId]}
					isVisible={projectDetails.inspectionTypeId === config.supportedTemplates.exxonMobilFVIPiping && role.approveReports}
					subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisationName} FVI Piping Reports`}
				/>
			)}

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm
					projectTitle={projectDetails?.projectTitle}
					onAdd={setReportTitle}
				/>
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm
					initialData={coverData}
					onAddCoverForm={setCoverData}
				/>
			</Zippy>

			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<div className="d-flex justify-content-end mt-2">
					<Switch 
						checked={includeSummary === 'include'}
						label='Include Inspection Breakdown'
						onChange={handleToggleSummary} 
					/>
				</div>
				
				<div className="my-4 divider"></div>

				<ExecutiveSummaryForm initialSummary={executiveSummary} onProceed={setExecutiveSummary} />
			</Zippy>

			<CustomCertificateForm calibrationRequired={false} certificates={certificates} onChangeCertificate={setCertificates} />

			<PipingItems pipingItems={pipingItems} onChangePipingItems={handleChangePipingItems} />

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving }
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export default CustomFVIPipingReport;
